import { useState, useRef, useEffect } from 'react';
import { ArrowUpDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useWalletAddress } from '@/hooks/useWalletAddress';
import { useUserPoints } from '@/hooks/useUserPoints';

interface PointsDisplayProps {
  className?: string;
}

export function PointsDisplay({ className }: PointsDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { isConnected } = useWalletAddress();
  const { userPoints, isLoading } = useUserPoints();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format points with commas
  const formatPoints = (points: number) => {
    return points.toLocaleString();
  };

  // Calculate values for display - always has default values (matching UserStats pattern)
  const selfPoints = userPoints?.self_point || 0;
  const backpackBonus = userPoints?.backpack_bonus_point || 0;
  const totalPoints = userPoints?.total_point || 0;

  // Don't show if wallet not connected - matching UserStats behavior
  if (!isConnected) {
    return null;
  }

  // Show loading skeleton ONLY while actively loading AND no data exists yet (matching UserStats behavior)
  // Once data loads (even if it's default 0 values), always show the component
  if (isLoading && !userPoints) {
    return (
      <div className={cn('flex items-center space-x-2 opacity-50', className)}>
        <div className="bg-muted h-8 w-16 animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Points Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center space-x-3 rounded-lg border px-4 py-2 transition-all duration-200',
          'backdrop-blur-sm hover:opacity-90',
          'text-sm font-medium max-md:h-[60px]'
        )}
        style={{
          backgroundColor: '#343434',
          borderColor: '#FEBB01',
          color: 'white',
        }}
      >
        <div className="flex items-center space-x-2">
          {/* Points Icon */}
          <img src="/points.svg" alt="points" width={16} height={16} />
          <div className="text-left">
            <div
              className="font-semibold"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                color: 'white',
              }}
            >
              {formatPoints(totalPoints)} PTS
            </div>
          </div>
        </div>

        <ArrowUpDown
          className="h-3 w-3"
          style={{ color: 'white', opacity: 0.7 }}
        />
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 z-[100] mt-2 w-80 rounded-lg p-4 shadow-xl md:right-0 md:left-auto"
          style={{
            backgroundColor: '#343434',
            border: '2px solid #FEBB01',
            boxShadow: '0 0 20px rgba(254, 187, 1, 0.3)',
          }}
        >
          {/* Header */}
          <div className="mb-4 flex items-center justify-between">
            <h3
              className="font-semibold"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 600,
                fontSize: '16px',
                color: 'white',
              }}
            >
              YOUR POINTS
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="rounded p-1"
              style={{ color: 'white' }}
              onMouseEnter={e =>
                (e.currentTarget.style.backgroundColor =
                  'rgba(255, 255, 255, 0.1)')
              }
              onMouseLeave={e =>
                (e.currentTarget.style.backgroundColor = 'transparent')
              }
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Balance Display */}
          <div className="mb-6 space-y-3">
            <div
              className="rounded-lg p-4"
              style={{
                backgroundColor: 'rgba(254, 187, 1, 0.1)',
                border: '1px solid rgba(254, 187, 1, 0.3)',
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div
                    className="text-sm"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 500,
                      color: '#FEBB01',
                    }}
                  >
                    Total Points
                  </div>
                  <div
                    className="text-lg font-bold"
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 600,
                      color: 'white',
                    }}
                  >
                    {formatPoints(totalPoints)}{' '}
                    <span
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      PTS
                    </span>
                  </div>
                </div>
                <img src="/points.svg" alt="points" width={24} height={24} />
              </div>
              <div
                className="mt-2 space-y-1 text-xs"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 500,
                  color: 'rgba(255, 255, 255, 0.7)',
                }}
              >
                <div>
                  Self Points: {formatPoints(selfPoints)}{' '}
                  <span
                    style={{
                      fontFamily: 'Outfit, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    PTS
                  </span>
                </div>
                {backpackBonus > 0 && (
                  <div style={{ color: '#50E4F9' }}>
                    ⭐ Backpack Bonus: {formatPoints(backpackBonus)}{' '}
                    <span
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontWeight: 500,
                      }}
                    >
                      PTS
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div
              className="rounded p-2 text-xs"
              style={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 400,
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              💡 Earn points by opening treasure chests. Invite friends to earn
              even more points!
            </div>
          </div>

          {/* Info Section */}
          <div className="space-y-3">
            <div
              className="rounded-lg px-3 py-4 text-center"
              style={{
                backgroundColor: 'rgba(254, 187, 1, 0.1)',
                border: '2px solid #FEBB01',
              }}
            >
              <div
                className="mb-2 text-sm font-medium"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 600,
                  color: '#FEBB01',
                }}
              >
                LOTTERY SYSTEM ACTIVE
              </div>
              <div
                className="text-xs"
                style={{
                  fontFamily: 'Outfit, sans-serif',
                  fontWeight: 400,
                  color: 'rgba(255, 255, 255, 0.9)',
                }}
              >
                Visit the Points page to see lottery tiers, contribute points,
                and view your chances to win!
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
